/**
 * Build timeline sub-items for JSON property values.
 *
 * When a property has field_type_name='json' and a companion schema_property,
 * we decompose the JSON object into individual sub-items — one per schema field —
 * instead of showing a raw JSON string.
 *
 * Entity-ref fields (x-entity-type) render as EntityBadge when the referenced
 * object is available in the timeline lookups.
 */

import { Edit03 as EditIcon } from '@untitledui/icons';
import type { ReactElement } from 'react';

import {
  type JsonSchemaField,
  parseJsonSchemaFields,
} from '@/lib/crm/hooks/useJsonSchemaFields';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';

import type { InternalPayload, ViewStateLookups } from '../types';
import type { SubItemContent } from '../types/renderer';
import { EntityRefBadge, HydratedEntityRef } from './EntityRefBadge';

/**
 * Look up the companion JSON Schema for a json-typed property.
 *
 * Reads from the event's internal_payload.companion_schemas, which is
 * snapshotted at write time by PayloadHydrator. This ensures the schema
 * matches what was active when the event was created, not the current
 * schema (which may have changed since).
 */
export function getCompanionSchema(
  schema: PropertyTypeSchema | undefined,
  internalPayload: InternalPayload | undefined
): Record<string, unknown> | null {
  if (schema?.field_type_name !== 'json' || !schema.schema_property) {
    return null;
  }
  const companionSchemas = internalPayload?.companion_schemas as
    | Record<string, Record<string, unknown>>
    | undefined;
  const companionValue = companionSchemas?.[schema.schema_property];
  if (
    companionValue &&
    typeof companionValue === 'object' &&
    !Array.isArray(companionValue)
  ) {
    return companionValue as Record<string, unknown>;
  }
  return null;
}

/**
 * Build sub-items for a single JSON property by decomposing it using the
 * companion schema.
 */
export function buildJsonSubItems(
  jsonValue: Record<string, unknown>,
  companionSchema: Record<string, unknown>,
  lookups: ViewStateLookups,
  onEntityClick?: (entityId: string) => void
): SubItemContent[] {
  const fields = parseJsonSchemaFields(companionSchema);
  const items: SubItemContent[] = [];

  for (const field of fields) {
    const value = jsonValue[field.key];
    if (value === undefined || value === null) {
      continue;
    }
    items.push(buildFieldSubItem(field, value, lookups, onEntityClick));
  }

  return items;
}

const ICON_CLASS = 'w-4 h-4 text-primary';

function buildFieldSubItem(
  field: JsonSchemaField,
  value: unknown,
  lookups: ViewStateLookups,
  onEntityClick?: (entityId: string) => void
): SubItemContent {
  const icon: ReactElement = <EditIcon className={ICON_CLASS} />;

  // Entity ref — single value
  if (
    field.refType === 'entity' &&
    !field.isMulti &&
    typeof value === 'string'
  ) {
    return {
      icon,
      text: (
        <span>
          {field.label}: {renderEntityRef(value, lookups, onEntityClick)}
        </span>
      ),
      ariaLabel: `${field.label}: ${lookups.entities[value]?.ahoy_display_name ?? value}`,
    };
  }

  // Entity ref — multi value
  if (field.refType === 'entity' && field.isMulti && Array.isArray(value)) {
    return {
      icon,
      text: (
        <span className='inline-flex flex-wrap items-center gap-1'>
          {field.label}:{' '}
          {value.map(
            (id, i) =>
              typeof id === 'string' && (
                <span key={id || i}>
                  {renderEntityRef(id, lookups, onEntityClick)}
                </span>
              )
          )}
        </span>
      ),
      ariaLabel: `${field.label}: ${value.length} items`,
    };
  }

  // Plain value
  return {
    icon,
    text: (
      <span>
        {field.label}: <span className='font-medium'>{String(value)}</span>
      </span>
    ),
    ariaLabel: `${field.label}: ${String(value)}`,
  };
}

function renderEntityRef(
  entityId: string,
  lookups: ViewStateLookups,
  onEntityClick?: (entityId: string) => void
): React.ReactNode {
  const obj = lookups.entities[entityId];
  if (obj) {
    return (
      <EntityRefBadge
        entityId={entityId}
        obj={obj}
        onEntityClick={onEntityClick}
      />
    );
  }
  // Not in the per-page lookup pool — hydrate the display name from the
  // CRM store (fetching on demand) rather than showing a truncated UUID.
  return <HydratedEntityRef entityId={entityId} />;
}
